import { useState } from 'react';
import { useAccount, useSwitchNetwork } from 'wagmi';
import { NetworkAbstractIcon, NetworkAvaxIcon, NetworkBNBIcon } from '@/components/icons/NetworkIcons';
import { logger } from '@/utils/logger';
import NetworkLoadingScreen from '@/components/NetworkLoadingScreen';

interface Network {
  name: string;
  icon: React.ReactNode;
  delay: number;
  themeName: string; // Theme to apply when clicked
  chainId?: number; // Chain ID for EVM networks (undefined for Solana/SUI)
  iconPath?: string; // Icon path for loading screen
}

const networks: Network[] = [
  {
    name: 'Abstract',
    icon: <NetworkAbstractIcon className="w-12 h-12" />,
    delay: 0,
    themeName: 'Abstract',
    chainId: 2741,
    iconPath: '/Crypto Logos/Abstract-logo.png'
  },
  {
    name: 'Arbitrum',
    icon: <img src="/Crypto Logos/Arbitrum logo custom.png" alt="Arbitrum" className="w-12 h-12" />,
    delay: 0.2,
    themeName: 'King Blue',
    chainId: 42161,
    iconPath: '/Crypto Logos/Arbitrum logo custom.png'
  },
  {
    name: 'Avalanche',
    icon: <NetworkAvaxIcon className="w-12 h-12" />,
    delay: 0.4,
    themeName: 'AVX',
    chainId: 43114,
    iconPath: '/Crypto Logos/AVAX.png'
  },
  {
    name: 'Base',
    icon: <img src="/Crypto Logos/BASE.png" alt="Base" className="w-12 h-12" />,
    delay: 0.6,
    themeName: 'Base',
    chainId: 8453,
    iconPath: '/Crypto Logos/BASE.png'
  },
  {
    name: 'BSC',
    icon: <NetworkBNBIcon className="w-12 h-12" />,
    delay: 0.8,
    themeName: 'BSCChain',
    chainId: 56,
    iconPath: '/Crypto Logos/BNB_chain.png'
  },
  {
    name: 'Ethereum',
    icon: <img src="/Crypto Logos/ETH.png" alt="Ethereum" className="w-12 h-12" />,
    delay: 1.0,
    themeName: 'ETH',
    chainId: 1,
    iconPath: '/Crypto Logos/ETH.png'
  },
  {
    name: 'opBNB',
    icon: <img src="/Crypto Logos/opBNB logo MDBAPP.IO.png" alt="opBNB" className="w-12 h-12" />,
    delay: 0.9,
    themeName: 'BSCChain',
    chainId: 204,
    iconPath: '/Crypto Logos/opBNB logo MDBAPP.IO.png'
  },
  {
    name: 'Optimism',
    icon: <img src="/Crypto Logos/OP.png" alt="Optimism" className="w-12 h-12" />,
    delay: 1.2,
    themeName: 'OP',
    chainId: 10,
    iconPath: '/Crypto Logos/OP.png'
  },
  {
    name: 'Polygon',
    icon: <img src="/Crypto Logos/MATIC.png" alt="Polygon" className="w-12 h-12" />,
    delay: 1.4,
    themeName: 'Poly',
    chainId: 137,
    iconPath: '/Crypto Logos/MATIC.png'
  },
  {
    name: 'Sei',
    icon: <img src="/Crypto Logos/Sei Custom Logo MdbApp.io.png" alt="Sei" className="w-12 h-12" />,
    delay: 1.5,
    themeName: 'Blade',
    chainId: 1329,
    iconPath: '/Crypto Logos/Sei Custom Logo MdbApp.io.png'
  },
  {
    name: 'Solana',
    icon: <img src="/Crypto Logos/SOL.png" alt="Solana" className="w-12 h-12" />,
    delay: 1.6,
    themeName: 'Don', // Using Don theme for Solana
    iconPath: '/Crypto Logos/SOL.png'
    // No chainId - non-EVM network
  },
  {
    name: 'SUI',
    icon: <img src="/Crypto Logos/SUI.png" alt="SUI" className="w-12 h-12" />,
    delay: 1.6,
    themeName: 'King Blue', // Using King Blue theme for SUI
    iconPath: '/Crypto Logos/SUI.png'
    // No chainId - non-EVM network
  }
];

export const NetworkCarousel = () => {
  const { isConnected } = useAccount();
  const { switchNetwork } = useSwitchNetwork();
  
  // State for network loading screen
  const [showNetworkLoading, setShowNetworkLoading] = useState(false);
  const [loadingNetworkIcon, setLoadingNetworkIcon] = useState<string>('');
  const [loadingNetworkName, setLoadingNetworkName] = useState<string>('');

  /**
   * Apply a theme based on network selection
   * Uses the same mechanism as network switching in NetworkSwitch component
   */
  const applyNetworkTheme = (themeName: string, networkName: string) => {
    try {
      // Dispatch custom event that the Header component listens to
      const event = new CustomEvent('applyNetworkTheme', {
        detail: { themeName }
      });
      window.dispatchEvent(event);
      
      console.log(`Applied ${themeName} theme for ${networkName}`);
    } catch (error) {
      console.error('Failed to apply network theme:', error);
    }
  };

  /**
   * Handle network click - switches network if wallet connected, otherwise just changes theme
   */
  const handleNetworkClick = async (network: Network) => {
    // Handle non-EVM networks (Solana, SUI) - redirect to their token launchers
    if (!network.chainId) {
      // Apply theme first
      applyNetworkTheme(network.themeName, network.name);
      
      // Redirect to appropriate token launcher
      if (network.name === 'Solana') {
        window.location.href = '/solanatokenlauncherapp';
      } else if (network.name === 'SUI') {
        window.location.href = '/suitokenlauncher';
      }
      return;
    }

    // Always apply theme first for EVM networks
    applyNetworkTheme(network.themeName, network.name);

    // If wallet is connected and network has a chainId, switch the network
    if (isConnected && switchNetwork) {
      try {
        logger.info(`Switching to ${network.name} (Chain ID: ${network.chainId})`);
        
        // Show network loading screen with network icon
        setLoadingNetworkIcon(network.iconPath || '/Dynamic Header Logos/MdbApp.io Default Logo.png');
        setLoadingNetworkName(network.name);
        setShowNetworkLoading(true);
        
        // Store network change info for potential refresh handling
        localStorage.setItem('lastNetworkChange', JSON.stringify({
          timestamp: Date.now(),
          toChainId: network.chainId
        }));
        
        // Switch network
        await switchNetwork(network.chainId);
        
        logger.info(`Successfully switched to ${network.name}`);
      } catch (error: any) {
        logger.error(`Failed to switch network: ${error.message}`);
        setShowNetworkLoading(false);
      }
    }
  };

  return (
    <div className="pt-4 pb-8 px-4">
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
      <div className="container mx-auto">
        <div className="flex flex-col items-center justify-center gap-6">
          <h3 className="text-lg font-semibold text-muted-foreground">
            Supported Networks
          </h3>
          
          {/* Networks Row */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {networks.map((network, index) => (
              <div
                key={index}
                className="flex flex-col items-center justify-center gap-3 float-animation cursor-pointer hover:scale-110 transition-transform duration-300"
                style={{ animationDelay: `${network.delay}s` }}
                onClick={() => handleNetworkClick(network)}
                title={
                  !network.chainId 
                    ? `Click to go to ${network.name} token launcher`
                    : isConnected 
                      ? `Click to switch to ${network.name} network` 
                      : `Click to switch to ${network.name} theme`
                }
              >
                <div className="transition-all duration-300">
                  {network.icon}
                </div>
                <span 
                  className="text-sm md:text-base font-bold"
                  style={{ color: 'var(--color-header-accent, #0A74FF)' }}
                >
                  {network.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Network Loading Screen Easter Egg */}
      {showNetworkLoading && (
        <NetworkLoadingScreen
          networkIcon={loadingNetworkIcon}
          networkName={loadingNetworkName}
          onComplete={() => setShowNetworkLoading(false)}
        />
      )}
    </div>
  );
};
